import { css } from "@emotion/react"
import { ComponentProps } from "react"
import { LegacyHeading as SharedHeading } from "./LegacyHeading"
import { Button as SharedButton } from "./Button"
import { Icon as SharedIcon } from "./Icon"

interface RootProps extends React.HTMLAttributes<HTMLElement> {
  className?: string
}

function Root({ className, children, ...props }: RootProps) {
  return (
    <section css={styles.root} className={className} {...props}>
      {children}
    </section>
  )
}

function Icon({ symbol }: { symbol: string }) {
  return (
    <div css={styles.icon}>
      <SharedIcon symbol={symbol} aria-hidden />
    </div>
  )
}

function Content({ children }: { children: React.ReactNode }) {
  return <div css={styles.content}>{children}</div>
}

interface HeadingProps {
  text: string
  level?: ComponentProps<typeof SharedHeading>["level"]
}

function Heading({ text, level = 2 }: HeadingProps) {
  return (
    <div css={styles.heading}>
      <SharedHeading level={level} size={3} text={text} />
    </div>
  )
}

function Description({ text }: { text: string }) {
  return <p css={styles.description}>{text}</p>
}

type ButtonProps = Pick<ComponentProps<typeof SharedButton>, "onClick" | "text">

function Button({ onClick, text }: ButtonProps) {
  return (
    <SharedButton variant="outline" size="sm" text={text} onClick={onClick} />
  )
}

/**
 * Compound component for empty/error states.
 * Optional `role="alert"` can be added to Root for screen readers to automatically announce error states.
 * Optional `level` prop can be added to Heading to adjust the underlying h1-h6 HTML element used. (Default is h2)
 *
 * @example
 * <BlankState.Root role="alert">
 *   <BlankState.Icon symbol="general#exclamation-triangle" />
 *   <BlankState.Content>
 *     <BlankState.Heading text="Error loading podcast" level={3} />
 *     <BlankState.Description text="Please check the feed URL and try again" />
 *   </BlankState.Content>
 *   <BlankState.Button text="Retry" onClick={retryHandler} />
 * </BlankState.Root>
 */
const BlankState = {
  Root,
  Icon,
  Content,
  Heading,
  Description,
  Button,
}

export { BlankState }

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
    margin: 0 auto;
    padding: 16px 0;
    max-width: 460px;
  `,
  icon: css`
    font-size: 32px;
    line-height: 0;
    color: var(
      --d-fill-color-neutral-020
    ); // TODO: Replace with icon token when available
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
  `,
  heading: css`
    & > :is(h1, h2, h3, h4, h5, h6) {
      color: var(--d-text-color);
    }
  `,
  description: css`
    color: var(--d-text-color-secondary);
    line-height: 1.3;
    margin: 0;
  `,
}
